import { Check, Minus } from 'lucide-react';
import { useEffect, useRef, type ChangeEvent, type ReactNode } from 'react';
import styles from './Checkbox.module.css';

// The native input stays in the DOM, moved off-screen rather than hidden, because
// a `display: none` input is not focusable and the keyboard has to reach it.

interface CheckboxProps {
  readonly checked: boolean;
  /** The table header's third state: some rows on this page selected, not all. */
  readonly isIndeterminate?: boolean;
  /**
   * The originating event comes back too, so a caller can see modifier keys. The
   * table needs it for shift-click ranges: reading the modifier here is reliable,
   * whereas cancelling the toggle from a capture-phase handler is not — React
   * derives its synthetic change from the click and fires it regardless.
   */
  readonly onChange: (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;
  readonly disabled?: boolean;
  readonly label?: string;
  readonly ariaLabel?: string;
}

/** True when a change was driven by a shift-click rather than the keyboard. */
export function isShiftClick(event: ChangeEvent<HTMLInputElement>): boolean {
  return event.nativeEvent instanceof MouseEvent && event.nativeEvent.shiftKey;
}

export function Checkbox({
  checked,
  isIndeterminate = false,
  onChange,
  disabled = false,
  label,
  ariaLabel,
}: CheckboxProps): ReactNode {
  const inputRef = useRef<HTMLInputElement | null>(null);

  // `indeterminate` is a DOM property with no HTML attribute, so it can only be
  // set imperatively.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = isIndeterminate;
  }, [isIndeterminate]);

  return (
    <label className={styles.wrap} data-disabled={disabled}>
      <input
        ref={inputRef}
        type="checkbox"
        className={styles.input}
        checked={checked}
        disabled={disabled}
        aria-label={ariaLabel ?? label}
        onChange={(event) => onChange(event.target.checked, event)}
      />
      <span className={styles.box} aria-hidden>
        {isIndeterminate ? <Minus size={12} strokeWidth={3} /> : <Check size={12} strokeWidth={3} />}
      </span>
      {label !== undefined && <span className={styles.label}>{label}</span>}
    </label>
  );
}

interface RadioProps {
  readonly name: string;
  readonly value: string;
  readonly checked: boolean;
  readonly onChange: (value: string) => void;
  readonly disabled?: boolean;
  /** Text, or text with a chip beside it (the merge page's stage, Redesign Group 8). */
  readonly label: ReactNode;
  readonly description?: string;
}

/**
 * A radio with room for a description — rejection reasons and decision rules both
 * need the user to understand what they are choosing, not just its name.
 */
export function Radio({
  name,
  value,
  checked,
  onChange,
  disabled = false,
  label,
  description,
}: RadioProps): ReactNode {
  return (
    <label
      className={`${styles.groupItem} ${checked ? styles.groupItemSelected : ''}`}
      data-disabled={disabled}
    >
      <input
        type="radio"
        name={name}
        value={value}
        className={styles.input}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange(value)}
      />
      <span className={`${styles.box} ${styles.radio}`} aria-hidden>
        <Check size={11} strokeWidth={3} />
      </span>
      <span className={styles.groupItemText}>
        <span className={styles.label}>{label}</span>
        {description !== undefined && <span className={styles.description}>{description}</span>}
      </span>
    </label>
  );
}

/** `isInline` (R9, QoL §7 rule 2): short choices side by side, wrapping on a narrow screen. */
export function RadioGroup({ children, isInline = false }: { children: ReactNode; isInline?: boolean }): ReactNode {
  return (
    <div className={`${styles.group} ${isInline ? styles.groupInline : ''}`} role="radiogroup">
      {children}
    </div>
  );
}
